<template>
  <el-dialog
    :model-value="visible"
    :title="listing ? `门店商品配置 · ${listing.productName}` : '门店商品配置'"
    width="860px"
    @update:model-value="emit('update:visible', $event)"
  >
    <el-skeleton v-if="loading && !listing" :rows="6" animated />
    <template v-else-if="listing">
      <div class="listing-summary">
        <div><span>门店</span><strong>{{ storeName }}</strong></div>
        <div>
          <span>上架状态</span>
          <el-select v-model="form.status">
            <el-option label="上架" value="ON" />
            <el-option label="下架" value="OFF" />
          </el-select>
        </div>
        <div>
          <span>排序</span>
          <el-input-number v-model="form.sort" :min="0" controls-position="right" />
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>SKU</th>
              <th>规格</th>
              <th>平台价（元）</th>
              <th>本店价（元）</th>
              <th>会员价（元）</th>
              <th>库存</th>
              <th>安全库存</th>
              <th>启用</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sku in form.skus" :key="sku.skuId">
              <td>{{ sku.skuCode || sku.skuId }}</td>
              <td>{{ sku.specText || '默认' }}</td>
              <td>{{ formatFen(sku.basePrice) }}</td>
              <td><el-input-number v-model="sku.priceYuan" :min="0" :precision="2" :step="1" controls-position="right" /></td>
              <td><el-input-number v-model="sku.memberPriceYuan" :min="0" :precision="2" :step="1" controls-position="right" /></td>
              <td><el-input-number v-model="sku.stock" :min="0" :precision="0" controls-position="right" /></td>
              <td><el-input-number v-model="sku.safetyStock" :min="0" :precision="0" controls-position="right" /></td>
              <td><el-switch v-model="sku.enabled" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template #footer>
      <el-button @click="emit('update:visible', false)">取消</el-button>
      <el-button v-if="listing?.status !== 'NOT_LISTED'" type="danger" plain :loading="submitting" @click="emit('remove')">下架</el-button>
      <el-button type="primary" :loading="submitting" :disabled="!listing" @click="submit">保存并上架</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue'
import { fenToYuan, yuanToFen } from '@jiaofa/shared-utils'
import type { ProductListing, ProductListingSavePayload } from '@/api/product'

interface ListingSkuForm {
  skuId: string
  skuCode?: string
  specText?: string
  basePrice?: number
  priceYuan: number
  memberPriceYuan: number
  stock: number
  safetyStock: number
  enabled: boolean
}

const props = defineProps<{
  visible: boolean
  listing?: ProductListing
  storeName: string
  loading: boolean
  submitting: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  save: [payload: ProductListingSavePayload]
  remove: []
}>()

const form = reactive({ status: 'ON' as 'ON' | 'OFF', sort: 0, skus: [] as ListingSkuForm[] })

watch(
  () => props.listing,
  (listing) => {
    if (!listing) return
    form.status = listing.status === 'OFF' ? 'OFF' : 'ON'
    form.sort = listing.sort ?? 0
    form.skus = listing.skus.map((sku) => ({
      skuId: sku.skuId,
      skuCode: sku.skuCode,
      specText: sku.specText,
      basePrice: sku.basePrice,
      priceYuan: Number(fenToYuan(sku.price ?? sku.basePrice ?? 0)),
      memberPriceYuan: Number(fenToYuan(sku.memberPrice ?? sku.baseMemberPrice ?? sku.price ?? sku.basePrice ?? 0)),
      stock: sku.stock ?? 0,
      safetyStock: sku.safetyStock ?? 0,
      enabled: sku.enabled !== false,
    }))
  },
  { immediate: true },
)

function formatFen(value?: number): string {
  return value === undefined ? '--' : Number(fenToYuan(value)).toFixed(2)
}

function submit(): void {
  if (!props.listing) return
  emit('save', {
    storeId: props.listing.storeId,
    status: form.status,
    sort: form.sort,
    skuPrices: form.skus.map((sku) => ({
      skuId: sku.skuId,
      price: yuanToFen(sku.priceYuan),
      memberPrice: yuanToFen(sku.memberPriceYuan),
      enabled: sku.enabled,
    })),
    skuStocks: form.skus.map((sku) => ({
      skuId: sku.skuId,
      stock: sku.stock,
      safetyStock: sku.safetyStock,
    })),
  })
}
</script>

<style scoped lang="scss">
.listing-summary { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 16px; margin-bottom: 18px; }
.listing-summary > div { display: flex; flex-direction: column; gap: 8px; }
.listing-summary span { color: var(--jf-color-text-secondary); font-size: 13px; }
.table-wrap { overflow-x: auto; border: 1px solid var(--jf-color-border); border-radius: 10px; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 13px; }
th, td { padding: 12px; border-bottom: 1px solid var(--jf-color-border); text-align: left; }
thead { background: var(--jf-color-background); color: var(--jf-color-text-secondary); }
tbody tr:last-child td { border-bottom: 0; }
</style>
